/*
 * File: cell-action-add.ts
 * Created: 2025-12-07 10:03:03
 * Author: Val Liu <valuis0429@gmail.com>
 * -----
 * Last Modified: 2026-01-16 22:28:13
 * Modified By: Val Liu <valuis0429@gmail.com>
 */

// directives/cell-action-add.ts
import { VNode, DirectiveOptions } from "vue";
import IvAutoModal from "@root/components/modals/auto-modal/auto-modal";
import IvForm from "@root/components/form/core/form";
import { IQuickUnit, QuickHelper } from "@root/core/utilities/quick-helper";
import { withMutedPropWarn } from "@root/core/utilities/with-muted-prop-warn";

//     <iv-auto-modal
//         :visible="visible"
//         @update:visible="update.visible"
//         label="w_UserCreateNew"
//         size="lg"
//     >
//         <iv-form
//             v-bind="QuickHelper.forTableC(quicks().User)"
//             @updated="update.visible(false)"
//         />
//     </iv-auto-modal>

/**
 * 新需求：
 * - 不管 directive 掛在任何 DOM 上
 * - 只要 click 就在 body 動態加入 IvAutoModal + IvForm
 * - IvForm updated:
 *    1) 關閉 modal（destroy）✅
 *    2) 可呼叫使用者傳入 updated ✅
 */

type BindingValue =
    | any
    | {
          quick: IQuickUnit;
          label?: string;
          size?: string;
          value?: any;
          updated?: (...args: any[]) => void;
      };

function normalizeBinding(value: BindingValue) {
    if (value && typeof value === "object" && "quick" in value) {
        return {
            quick: (value as any).quick,
            label: (value as any).label as string | undefined,
            size: (value as any).size as string | undefined,
            value: (value as any).value,
            updated: (value as any).updated as ((...args: any[]) => void) | undefined,
        };
    }
    return {
        quick: value,
        label: undefined,
        size: undefined,
        value: undefined,
        updated: undefined,
    };
}

/** ---------- module-level state (避免 this 問題) ---------- */
const handlers = new WeakMap<HTMLElement, (e: MouseEvent) => void>();
const latestValues = new WeakMap<HTMLElement, BindingValue>();

/**
 * 動態掛一個暫時 Vue instance 到 body
 */
function openAddModal(opts: {
    quick: any;
    label: string;
    size: string;
    value?: any;
    updated?: (...args: any[]) => void;
}) {
    const { quick, label, size, value, updated } = opts;

    const mountEl = document.createElement("div");
    document.body.appendChild(mountEl);

    let vm: any = null;

    const destroy = () => {
        if (vm) {
            vm.$destroy();
            vm = null;
        }
        if (mountEl.parentNode) {
            mountEl.parentNode.removeChild(mountEl);
        }
    };

    // 用 Vue.extend/直接 new Vue 都可
    // 這裡用 require 動態避免你專案 alias 差異問題也行
    // 但你已經全域 Vue2，直接 new (Vue as any) OK
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const VueCtor = require("vue").default || require("vue");

    vm = new VueCtor({
        render(h: any) {
            const handleUpdated = (...args: any[]) => {
                // 1) 先回呼外部
                try {
                    updated?.(...args);
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error("[v-cell-action-add] updated error:", e);
                }

                // 2) 關閉/銷毀
                destroy();
            };

            const handleVisibleUpdate = (v: boolean) => {
                if (!v) destroy();
            };

            return h(
                IvAutoModal,
                {
                    props: {
                        visible: true,
                        label,
                        size,
                    },
                    on: {
                        "update:visible": handleVisibleUpdate,
                    },
                },
                [
                    h(IvForm, {
                        props: {
                            value,
                        },
                        attrs: {
                            ...QuickHelper.forTableC(quick),
                        },
                        on: {
                            updated: handleUpdated,
                        },
                    }),
                ],
            );
        },
    });

    vm.$mount(mountEl);

    return { destroy };
}

function resolveLabel(bindingValue: BindingValue, vnode?: VNode) {
    const n = normalizeBinding(bindingValue);
    if (n.label) return n.label;
    let quick = n.quick;

    const labelFromProps = (vnode?.componentOptions?.propsData as any)?.label;
    if (labelFromProps) return labelFromProps;

    return `{w_QuickList_CreateItem("${quick.title}")}`;
}

function applyAutoLabel(bindingValue: BindingValue, vnode?: VNode) {
    if (!vnode || !vnode.componentInstance) return;

    const vm: any = vnode.componentInstance;
    const propsOptions = (vm.$options && vm.$options.props) || {};
    const hasLabelProp = Object.prototype.hasOwnProperty.call(propsOptions, "label");

    // 這個元件根本沒有 label prop，就不用管了
    if (!hasLabelProp) return;

    // 呼叫端有明確傳 label，就不要覆蓋
    const propsData = (vnode.componentOptions && vnode.componentOptions.propsData) || {};
    if (Object.prototype.hasOwnProperty.call(propsData, "label")) {
        return;
    }

    // 真的「沒有設定 prop label」→ 自動套用 resolveLabel
    const label = resolveLabel(bindingValue, vnode);

    // 這裡直接寫到子元件的 prop 上（雖然是 hack，但在 directive 情境可接受）
    withMutedPropWarn(() => (vm.label = label));
    vm.$forceUpdate();
}

function resolveSize(bindingValue: BindingValue) {
    const n = normalizeBinding(bindingValue);
    return n.size || "lg";
}

const CellActionAddDirective: DirectiveOptions = {
    bind(el: HTMLElement, binding: any, vnode: VNode) {
        latestValues.set(el, binding.value);

        const handler = (e: MouseEvent) => {
            // 阻止 bubble up
            e.stopPropagation();
            e.stopImmediatePropagation?.();

            // 只在 <a> 才 preventDefault，避免 href="#" 或連結預設行為
            if (el instanceof HTMLAnchorElement) {
                e.preventDefault();
            }

            const latest = latestValues.get(el) ?? binding.value;
            const n = normalizeBinding(latest);

            const label = resolveLabel(latest, vnode);
            const size = resolveSize(latest);

            openAddModal({
                quick: n.quick,
                label,
                size,
                value: n.value,
                updated: n.updated,
            });
        };

        handlers.set(el, handler);
        el.addEventListener("click", handler);
        // 初次掛載時試著幫目標元件自動補 label
        applyAutoLabel(binding.value, vnode);
    },

    update(el: HTMLElement, binding: any, vnode: VNode) {
        latestValues.set(el, binding.value);
        // 綁定值變動時（比如 quick / label 改了），同步更新元件的預設 label
        applyAutoLabel(binding.value, vnode);
    },

    unbind(el: HTMLElement) {
        const handler = handlers.get(el);
        if (handler) {
            el.removeEventListener("click", handler);
        }
        handlers.delete(el);
        latestValues.delete(el);
    },
};

export default CellActionAddDirective;
